import i18n from "@/lang/i18n"

// digit grouping from language
function groupingLocale(): string {
	return String(i18n.global.locale.value).startsWith("fr") ? "fr-CA" : "en-CA"
}

export function formatQuantity(value: number | null | undefined, options: Intl.NumberFormatOptions = {}): string {
	if (value == null || !Number.isFinite(value)) return ""
	return new Intl.NumberFormat(groupingLocale(), { useGrouping: true, ...options }).format(value)
}

// Match display separators: blank clears the field; invalid text leaves its value unchanged.
export function parseQuantityInput(value: string): number | null | undefined {
	const compact = value.replace(/\s/g, "")
	if (!compact) return null
	const parts = new Intl.NumberFormat(groupingLocale()).formatToParts(12345.6)
	const group = parts.find(part => part.type === "group")?.value
	const decimal = parts.find(part => part.type === "decimal")?.value ?? "."
	const ungrouped = group ? compact.split(group).join("") : compact
	const parsed = Number(ungrouped.replace(decimal, "."))
	return Number.isFinite(parsed) ? parsed : undefined
}

export function spaceOutNumbers(...values: Array<string | number | null | undefined>): string[] {
	return values
		.filter(Boolean)
		.map(String)
		.map(n => n.replace(/\B(?=(\d{3})+(?!\d))/g, " "))
}

export function roundTo(value: number | null | undefined, multiple: number): number | null {
	if (!value) return null
	return Math.round(value / multiple) * multiple
}

const cleanMultipliers = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]

export function roundToCleanNumber(value: number): number {
	if (value <= 0) return 0

	const magnitude = 10 ** Math.floor(Math.log10(value))
	const normalized = value / magnitude
	const nearest = cleanMultipliers.reduce((best, multiplier) =>
		Math.abs(multiplier - normalized) < Math.abs(best - normalized) ? multiplier : best
	)

	return Math.round(nearest * magnitude)
}

export function formatDecimal(value: number | null | undefined, decimals: number): string {
	if (value == null) return ""
	return value.toFixed(decimals)
}

export function formatInscriptionNumber(value: string | number | null | undefined): string {
	if (value == null) return ""

	const raw = String(value).trim()
	if (!raw) return ""

	const compact = raw.replace(/\s+/g, "")
	if (!/^\d+$/.test(compact)) return raw

	return compact.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
}

export function formatCompactNumber(value: number, locale = "fr-CA"): string {
	const format = (scaled: number, suffix: string): string =>
		`${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(scaled)}${suffix}`

	if (value >= 1_000_000 && value % 100_000 === 0) return format(value / 1_000_000, "M")
	if (value >= 1_000 && value % 100 === 0) return format(value / 1_000, "K")
	return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)
}

export function formatCurrency(value: number, locale: string, currency = "CAD"): string {
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency,
		maximumFractionDigits: 0,
	}).format(value)
}

// matches "45.5017, -73.5673" style raw coordinate pairs, not real street addresses
const COORDINATE_PATTERN = /^-?\d{1,3}(\.\d+)?\s*,\s*-?\d{1,3}(\.\d+)?$/

export function isCoordinateAddress(address: string | null | undefined): boolean {
	return !!address && COORDINATE_PATTERN.test(address.trim())
}

// borough is a subdivision of a municipality (e.g. Rosemont is part of Montréal), so it's the more
// specific part of the location and comes first, same as "street, city, province"
export function locationSubtitle(borough: string | null | undefined, municipality: string | null | undefined): string {
	return [borough, municipality].filter(Boolean).join(", ")
}

interface TransactionAddressLines {
	street: string | null
	cityLine: string | null
}

// some addresses already embed "<street>, <municipality>, <province>"; split that off into a second
// line instead of repeating it via the separate municipality/borough fields
export function splitTransactionAddress(
	address: string | null | undefined,
	borough: string | null | undefined,
	municipality: string | null | undefined
): TransactionAddressLines {
	const subtitle = locationSubtitle(borough, municipality)

	if (!address || isCoordinateAddress(address)) {
		return { street: null, cityLine: subtitle || null }
	}

	const segments = address
		.split(",")
		.map(s => s.trim())
		.filter(Boolean)
	// address segments can carry an administrative prefix (e.g. "Municipalité de X", "Ville de X"),
	// so match by substring rather than requiring an exact segment match
	const municipalityIndex = municipality
		? segments.findIndex(s => s.toLowerCase().includes(municipality.toLowerCase()))
		: -1

	if (municipalityIndex > 0) {
		return {
			street: segments.slice(0, municipalityIndex).join(", "),
			cityLine: segments.slice(municipalityIndex).join(", "),
		}
	}

	return { street: address, cityLine: subtitle || null }
}

export function toExternalHref(value: string | null | undefined): string | null {
	const trimmed = value?.trim()
	if (!trimmed) return null
	if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith("mailto:")) return trimmed
	return `https://${trimmed}`
}

export function formatDuration(durationMs: number): string {
	const totalSeconds = durationMs / 1000
	if (totalSeconds < 10) return `${totalSeconds.toFixed(1)}s`
	const roundedSeconds = Math.round(totalSeconds)
	const minutes = Math.floor(roundedSeconds / 60)
	const seconds = roundedSeconds % 60
	return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`
}
